// Подборки по цветку: /podborka/<slug>/ (ТЗ «реклама по наличию», В3, 29.10.2026).
//
// Состав отдаёт сервер (модуль рекламы по наличию):
//   GET /api/seo/collections/{slug} → { slug, title, items: [те же объекты, что /api/products] }
//   GET /api/seo/collections        → [{ slug, title, count }] — только где есть что купить сегодня
// Неизвестная подборка — 404. Витрина показывает items обычными карточками; пусто или 404 —
// окно «Сейчас нет в продаже» с похожими из других подборок.
//
// Заглушка: пока серверного API нет (или у магазина модуль выключен), сборка с
// REACT_APP_COLLECTIONS_STUB=1 собирает подборки из списка витрины тем же форматом.
// Без флага — только настоящий API, правок при его появлении не нужно.
import { api } from '../api';
import { flowerKind, inStockToday, interleave, SIMILAR_MAX } from './unavailable';

export const COLLECTIONS_STUB = process.env.REACT_APP_COLLECTIONS_STUB === '1';

// Стартовый набор из ТЗ (раздел 8): вид цветка — как flowerKind() (первое слово без окончания)
export const STUB_COLLECTIONS = [
  { slug: 'gvozdiki', title: 'Гвоздики', kinds: ['гвоздик'] },
  { slug: 'kally', title: 'Каллы', kinds: ['калл'] },
  { slug: 'rozy', title: 'Розы', kinds: ['роз'] },
  { slug: 'hrizantemy', title: 'Хризантемы', kinds: ['хризантем', 'сантин'] },
  { slug: 'gortenziya', title: 'Гортензия', kinds: ['гортензи'] },
  { slug: 'alstromeriya', title: 'Альстромерия', kinds: ['альстромери'] },
  { slug: 'eustoma', title: 'Эустома', kinds: ['эустом'] },
];

const notFound = () => Object.assign(new Error('Not Found'), { status: 404 });

export function stubCollection(slug, products) {
  const c = STUB_COLLECTIONS.find(x => x.slug === slug);
  if (!c) throw notFound();
  const items = (products || []).filter(p => inStockToday(p) && c.kinds.includes(flowerKind(p.name)));
  return { slug: c.slug, title: c.title, items };
}

export function stubCollections(products) {
  return STUB_COLLECTIONS
    .map(c => ({ slug: c.slug, title: c.title, count: stubCollection(c.slug, products).items.length }))
    .filter(c => c.count > 0);
}

const requests = new Map();   // slug → обещание ответа сервера: один запрос на подборку

function fetchCollection(slug, products) {
  if (COLLECTIONS_STUB) return Promise.resolve().then(() => stubCollection(slug, products));
  if (!requests.has(slug)) {
    const req = api.getCollection(slug);
    // Сбой сети не запоминаем — следующий заход спросит снова
    req.catch(err => { if (!err || err.status !== 404) requests.delete(slug); });
    requests.set(slug, req);
  }
  return requests.get(slug);
}

// Подборка для страницы: { found, title, items }. found=false — нет такой (404) или не достали.
export async function loadCollection(slug, products) {
  try {
    const c = await fetchCollection(slug, products);
    return { found: true, title: (c && c.title) || '', items: Array.isArray(c && c.items) ? c.items : [] };
  } catch (e) {
    return { found: false, title: '', items: [] };
  }
}

// «Есть сегодня» для пустой или неизвестной подборки: поровну из других подборок, где есть
// что купить. Не достали — пустой список (окно возьмёт самое ходовое из витрины).
export async function loadOtherCollectionsItems(excludeSlug, products, limit = SIMILAR_MAX) {
  try {
    const list = COLLECTIONS_STUB ? stubCollections(products) : await api.getCollections();
    const others = (Array.isArray(list) ? list : [])
      .filter(c => c && c.slug !== excludeSlug && (c.count == null || c.count > 0))
      .slice(0, 3);
    const results = await Promise.all(others.map(c => loadCollection(c.slug, products)));
    return interleave(results.map(r => r.items), limit);
  } catch (e) {
    return [];
  }
}
